const API_BASE      = 'https://kosmaro-classifi-func-deb5cdbwatcugjct.centralus-01.azurewebsites.net/api';
const STELARO_BASE  = 'https://kosmaro-stelaro-func-b0b4ccghb8bxgnaz.centralus-01.azurewebsites.net/api'; // Shared licensing for all Stelaro products
const PRODUCT_ID    = 'classifi'; // This app's product identifier

let accessToken: string | null = localStorage.getItem('classifi_access_token');
let userEmail: string = '';
let rulesCache: any[] = [];
let editingId: string | null = null;
let learnState = { sender: '', domain: '', subject: '' };

// Outlook master categories require a real preset color (None is rejected by OWA),
// so derive a stable Preset0-Preset24 color from the category name.
function colorForCategory(name: string): any {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = ((h * 31) + name.charCodeAt(i)) >>> 0;
  return (Office.MailboxEnums.CategoryColor as any)['Preset' + (h % 25)];
}

// Ensure categories exist in the mailbox master list (with valid colors) before applying to an item.
// Reads the existing master list and adds only the missing ones; proceeds even if the add step fails.
function ensureMasterCategories(names: string[], done: () => void) {
  try {
    Office.context.mailbox.masterCategories.getAsync((g: any) => {
      const have = new Set<string>();
      if (g && g.status === Office.AsyncResultStatus.Succeeded) {
        (g.value || []).forEach((cat: any) => have.add(cat.displayName));
      }
      const missing = names.filter(n => !have.has(n)).map(n => ({ displayName: n, color: colorForCategory(n) }));
      if (!missing.length) { done(); return; }
      Office.context.mailbox.masterCategories.addAsync(missing, (a: any) => {
        if (a && a.status === Office.AsyncResultStatus.Failed) {
          console.warn('masterCategories.addAsync failed:', a.error?.message, a.error?.code);
        }
        done();
      });
    });
  } catch (e) {
    console.warn('masterCategories.getAsync threw:', e);
    done();
  }
}
let toastTimer: ReturnType<typeof setTimeout>;
let isLicensed: boolean = false;
let licenseType: string = '';
let modelSeeded: boolean = false;

const FREE_RULE_LIMIT = 5;

/* ══ Auth ══ */
function showAuthError(message: string, helpText?: string) {
  document.getElementById('auth-screen')!.style.display = 'flex';
  document.getElementById('main-app')!.style.display    = 'none';
  const errEl  = document.getElementById('auth-error')!;
  const helpEl = document.getElementById('auth-help')!;
  const btn    = document.getElementById('auth-btn') as HTMLButtonElement;
  if (message) {
    errEl.style.display   = 'block';
    errEl.textContent     = message;
  } else {
    errEl.style.display   = 'none';
  }
  if (helpText) {
    helpEl.style.display  = 'block';
    helpEl.textContent    = helpText;
  } else {
    helpEl.style.display  = 'none';
  }
  btn.textContent = 'Retry Sign In';
}

async function initAuth() {
  try {
    // Get SSO token directly from Office
    accessToken = await Office.auth.getAccessToken({
      allowSignInPrompt:  true,
      allowConsentPrompt: true
    });
    userEmail = Office.context.mailbox?.userProfile?.emailAddress || '—';
    await showMainApp();
    await checkLicense();
    if (isLicensed) {
      registerWebhook();
      if (!modelSeeded) { seedModel(); modelSeeded = true; }
    }
  } catch(e: any) {
    console.error('SSO failed:', e);
    const code = e.code || e.errorCode || 0;

    // Codes where MSAL dialog fallback should be attempted instead of showing an error
    const fallbackCodes = [13006, 13007, 13009, 13012, 13000];
    if (fallbackCodes.includes(code) || code === 0) {
      console.log(`SSO code ${code} — attempting MSAL dialog fallback`);
      await initAuthWithDialog();
      return;
    }

    // Handle specific Office SSO error codes
    // See: https://learn.microsoft.com/en-us/office/dev/add-ins/develop/troubleshoot-sso-in-office-add-ins
    if (code === 13001) {
      showAuthError(
        'You are not signed in to Microsoft Office.',
        'Please sign in to your Microsoft 365 account in Outlook and try again.'
      );
    } else if (code === 13002) {
      showAuthError(
        'Permission was denied.',
        'Classifi needs permission to access your account. Please click Retry and accept the permissions prompt.'
      );
    } else if (code === 13003) {
      showAuthError(
        'This account type is not supported.',
        'Classifi requires a Microsoft 365 work or school account.'
      );
    } else if (code === 13004) {
      showAuthError(
        'Configuration error (13004).',
        'Please contact support at hello@kosmaro.com.'
      );
    } else if (code === 13005) {
      showAuthError(
        'Could not obtain sign-in token (13005).',
        'Your organization may require additional permissions. Please contact your IT administrator.'
      );
    } else if (code === 13008) {
      showAuthError(
        'Sign-in is already in progress. Please wait a moment and try again.',
        undefined
      );
    } else if (code === 13010) {
      showAuthError(
        'Sign-in unavailable in this environment (13010).',
        'Please contact support at hello@kosmaro.com.'
      );
    } else {
      // Unknown error — try MSAL fallback before giving up
      console.log(`Unknown SSO code ${code} — attempting MSAL dialog fallback`);
      await initAuthWithDialog();
    }
  }
}

// MSAL dialog fallback — used when SSO is unavailable or consent is needed
function initAuthWithDialog(): Promise<void> {
  return new Promise((resolve) => {
    const dialogUrl = `https://red-stone-08944080f.7.azurestaticapps.net/auth.html`;
    Office.context.ui.displayDialogAsync(
      dialogUrl,
      { height: 60, width: 30, promptBeforeOpen: false },
      (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          console.error('Dialog failed to open:', result.error);
          showAuthError(
            'Sign-in dialog could not be opened.',
            'Please try again or contact support at hello@kosmaro.com.'
          );
          resolve();
          return;
        }
        const dialog = result.value;
        dialog.addEventHandler(Office.EventType.DialogMessageReceived, async (args: any) => {
          dialog.close();
          try {
            const msg = JSON.parse(args.message);
            if (msg.type === 'msal-auth-complete' && msg.success && msg.accessToken) {
              accessToken    = msg.accessToken;
              usingMsalToken = true;
              localStorage.setItem('classifi_access_token', msg.accessToken);
              localStorage.setItem('classifi_using_msal', 'true');
              userEmail      = msg.username || Office.context.mailbox?.userProfile?.emailAddress || '—';
              await showMainApp();
              await checkLicense();
              if (isLicensed) {
                registerWebhook();
                if (!modelSeeded) { seedModel(); modelSeeded = true; }
              }
            } else {
              showAuthError(
                'Sign-in failed.',
                'Please try again or contact support at hello@kosmaro.com.'
              );
            }
          } catch (parseErr) {
            showAuthError('Sign-in failed.', 'Please try again.');
          }
          resolve();
        });
        dialog.addEventHandler(Office.EventType.DialogEventReceived, (args: any) => {
          // Log the actual error code so we can see what Office fires
          console.log('[Classifi] Dialog event code:', args.error);
          // 12003 = navigated to different domain (expected during loginRedirect to MS)
          // 12006 = dialog closed — but loginRedirect fires this spuriously during navigation
          // We do NOT close or resolve here. We wait for DialogMessageReceived.
          // The dialog navigates to MS login and back; messageParent fires when it returns.
        });
      }
    );
  });
}


// Track whether we're using MSAL token (no SSO) or SSO token
let usingMsalToken = localStorage.getItem('classifi_using_msal') === 'true';

/* == Durable session (roamingSettings) ==
 * roamingSettings lives in the Exchange mailbox, so unlike localStorage it
 * survives Outlook restarts and roams across devices. We persist an opaque
 * refreshKey (the backend exchanges it for fresh access tokens -- the real
 * Microsoft refresh token never leaves the server) plus the last validated
 * license result, so the UI is correct instantly and survives brief outages.
 */
let refreshKey: string | null = null;

const ROAMING_KEY      = 'classifiSession';
const LICENSE_GRACE_MS = 14 * 24 * 60 * 60 * 1000;

interface RoamingSession {
  email?:       string;
  refreshKey?:  string;
  accessToken?: string;
  licensed?:    boolean;
  licenseType?: string;
  validatedAt?: number;
}

function loadRoaming(): RoamingSession {
  try {
    const raw = Office.context.roamingSettings.get(ROAMING_KEY);
    if (!raw) return {};
    return typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch { return {}; }
}

function saveRoaming(patch: Partial<RoamingSession>): Promise<void> {
  return new Promise((resolve) => {
    try {
      const merged = { ...loadRoaming(), ...patch };
      Office.context.roamingSettings.set(ROAMING_KEY, JSON.stringify(merged));
      Office.context.roamingSettings.saveAsync(() => resolve());
    } catch { resolve(); }
  });
}

function clearRoaming(): void {
  try {
    Office.context.roamingSettings.remove(ROAMING_KEY);
    Office.context.roamingSettings.saveAsync(() => {});
  } catch { /* ignore */ }
}

// Ask the backend to mint a fresh access token from the stored (server-held)
// refresh token. Silent -- no UI. Returns the new token, or null if the session
// is gone (refreshKey unknown / refresh token revoked) so the caller can wall.
async function refreshAccessToken(): Promise<string | null> {
  if (!refreshKey) return null;
  try {
    const resp = await fetch(`${API_BASE}/authRelay?action=refresh`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ refreshKey })
    });
    if (resp.status === 401) {
      // Session no longer valid server-side -- drop our key and force sign-in.
      refreshKey = null;
      await saveRoaming({ refreshKey: undefined });
      return null;
    }
    if (!resp.ok) return null; // transient -- keep what we have
    const data = await resp.json();
    if (!data.accessToken) return null;
    accessToken    = data.accessToken;
    usingMsalToken = true;
    localStorage.setItem('classifi_access_token', data.accessToken);
    localStorage.setItem('classifi_using_msal', 'true');
    await saveRoaming({ accessToken: data.accessToken });
    return data.accessToken;
  } catch {
    return null; // network error -- keep any existing token / cached license
  }
}

// Called when the relay handoff returns a fresh token + refreshKey after sign-in.
async function persistSession(access: string, key?: string): Promise<void> {
  accessToken    = access;
  usingMsalToken = true;
  if (key) refreshKey = key;
  localStorage.setItem('classifi_access_token', access);
  localStorage.setItem('classifi_using_msal', 'true');
  await saveRoaming({
    email:       Office.context.mailbox?.userProfile?.emailAddress || '',
    accessToken: access,
    ...(key ? { refreshKey: key } : {})
  });
}


function triggerReAuth(reason?: string) {
  // Clear expired token and return user to sign-in screen
  accessToken    = null;
  usingMsalToken = false;
  refreshKey     = null;
  localStorage.removeItem('classifi_access_token');
  localStorage.removeItem('classifi_using_msal');
  saveRoaming({ refreshKey: undefined, accessToken: undefined });
  if (authPollTimer) { clearInterval(authPollTimer); authPollTimer = null; }

  document.getElementById('main-app')!.style.display    = 'none';
  document.getElementById('auth-screen')!.style.display = 'flex';

  const msgEl = document.getElementById('auth-message');
  if (msgEl && reason) msgEl.textContent = reason;
}

async function ensureToken() {
  if (usingMsalToken) {
    // Relay path: use the current token if still valid...
    if (accessToken && !isTokenExpired(accessToken)) return;
    // ...otherwise mint a new one silently from the backend before ever walling.
    const refreshed = await refreshAccessToken();
    if (refreshed) return;
    triggerReAuth('Your session has expired. Please sign in again.');
    throw new Error('Session expired — re-auth required');
  }
  try {
    accessToken = await (Office.auth as any).getAccessToken({
      allowSignInPrompt:  true,
      allowConsentPrompt: true,
      forMSGraphAccess:   false
    });
  } catch (e) {
    // SSO not available — use existing token if we have one from MSAL dialog
    if (!accessToken) throw e;
  }
}

async function checkLicense() {
  const email = Office.context.mailbox?.userProfile?.emailAddress || '';
  try {
    await ensureToken();
    const res  = await fetch(`${STELARO_BASE}/validateLicense`, {
      method:  'GET',
      headers: { Authorization: `Bearer ${accessToken}`, 'X-Stelaro-Product': PRODUCT_ID }
    });
    const data = await res.json();
    isLicensed  = data.licensed === true;
    licenseType = data.licenseType || '';
    await saveRoaming({ email, licensed: isLicensed, licenseType, validatedAt: Date.now() });
    updateLicenseUI();
  } catch(e) {
    console.warn('License check failed:', e);
    // Fall back to the last validated result for this mailbox within the grace
    // window, so a transient blip doesn't downgrade a paid user to "free".
    const cached = loadRoaming();
    if (cached.email === email && cached.licensed === true &&
        typeof cached.validatedAt === 'number' &&
        (Date.now() - cached.validatedAt) < LICENSE_GRACE_MS) {
      isLicensed  = true;
      licenseType = cached.licenseType || '';
    } else {
      isLicensed = false;
    }
    updateLicenseUI();
  }
}

function updateLicenseUI() {
  const pill = document.getElementById('license-status-pill');
  const bulkBtn = document.getElementById('bulk-btn') as HTMLButtonElement | null;
  if (bulkBtn && !bulkBtn.classList.contains('running')) bulkBtn.disabled = !isLicensed;
  const section = document.getElementById('license-section');
  if (pill) {
    if (isLicensed) {
      pill.textContent = '✦ Licensed';
      pill.className = 'license-pill licensed';
    } else {
      pill.textContent = `Free — ${rulesCache.length}/${FREE_RULE_LIMIT} rules`;
      pill.className = 'license-pill free';
    }
  }
  if (section) {
    section.innerHTML = isLicensed
      ? `<div class="license-active"><div class="la-icon">✦</div><div><div class="la-title">Classifi Licensed</div><div class="la-sub">${licenseType === 'upgrade' ? 'Upgrade license' : 'Lifetime license'} · Unlimited rules &amp; auto-categorization</div></div></div>`
      : `<div class="license-free">
          <div class="lf-info"><div class="lf-title">Free tier</div><div class="lf-sub">Up to ${FREE_RULE_LIMIT} rules · Manual apply only<br/>Auto-categorization requires a license</div></div>
          <a href="https://kosmaro.com/classifi.html#pricing" target="_blank" class="btn-accent btn-sm">Upgrade — $19</a>
         </div>
         <div class="license-entry">
           <div class="set-label">Have a license key?</div>
           <div class="license-input-row">
             <input type="text" id="license-key-input" placeholder="XXXXXX-XXXXXX-XXXXXX-XXXXXX" style="font-family:var(--mono);letter-spacing:1px;" />
             <button class="btn-accent" onclick="activateLicense()">Activate</button>
           </div>
           <div id="license-error" style="display:none;font-size:11px;color:#f87171;margin-top:6px;"></div>
         </div>`;
  }
}

async function registerWebhook() {
  // Auto-tagging now runs server-side via the mailbox poller (pollMailboxes), so the
  // client no longer registers a Graph subscription. Kept as a no-op so the existing
  // call sites don't need to change.
  return;
}


async function seedModel() {
  try {
    await ensureToken();
    await fetch(`${API_BASE}/train`, {
      method:  'POST',
      headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
      body:    JSON.stringify({ seedFromRules: true })
    });
    console.log('Bayesian model seeded from rules');
  } catch(e) {
    console.warn('Model seeding failed:', e);
  }
}

async function resetModel() {
  try {
    await ensureToken();
    const r = await fetch(`${API_BASE}/train`, {
      method:  'POST',
      headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
      body:    JSON.stringify({ reset: true })
    });
    const d = await r.json().catch(() => ({}));
    console.log('Classifi AI model reset + reseeded from rules:', d);
    toast('AI learning reset \u2713');
  } catch(e) { console.warn('Model reset failed:', e); toast('Reset failed.'); }
}
(window as any).resetClassifiModel = resetModel;

async function trainOnApply(sender: string, subject: string, categories: string[]) {
  try {
    await ensureToken();
    await fetch(`${API_BASE}/train`, {
      method:  'POST',
      headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
      body:    JSON.stringify({ sender, subject, categories })
    });
    console.log('Trained model on manual apply:', categories);
  } catch(e) {
    console.warn('Training failed:', e);
  }
}
(window as any).signIn = function() {
  // Nothing — the auth-btn is now an <a> link that opens login.html in a new tab.
  // onSignInLinkClicked() starts polling.
};

let authPollTimer: ReturnType<typeof setInterval> | null = null;

(window as any).onSignInLinkClicked = function() {
  // Generate a session ID, embed it in the login URL, then poll our backend for the token.
  // This bypasses all OWA sandbox restrictions (no localStorage sharing, no window.opener,
  // no popup access). login.html POSTs the token to the backend; we poll to retrieve it.
  const sessionId = (crypto as any).randomUUID ? (crypto as any).randomUUID() : 
    Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
  const loginUrl  = `https://red-stone-08944080f.7.azurestaticapps.net/login.html?session=${sessionId}`;

  // Update the link href so the new tab gets the session ID
  const link = document.getElementById('auth-btn') as HTMLAnchorElement;
  if (link) link.href = loginUrl;

  const statusEl = document.getElementById('auth-poll-status')!;
  statusEl.style.display = 'block';

  startPollingBackend(sessionId);
};

function startPollingBackend(sessionId: string): void {
  if (authPollTimer) clearInterval(authPollTimer);
  authPollTimer = setInterval(async () => {
    try {
      const resp = await fetch(`${API_BASE}/authRelay?action=poll&session=${sessionId}`);
      if (!resp.ok) return;
      const data = await resp.json();
      if (data.accessToken && !isTokenExpired(data.accessToken)) {
        clearInterval(authPollTimer!);
        authPollTimer    = null;
        await persistSession(data.accessToken, data.refreshKey);
        document.getElementById('auth-poll-status')!.style.display = 'none';
        await initAuthWithToken(data.accessToken);
      }
    } catch { /* ignore network errors, keep polling */ }
  }, 2000);
}
(window as any).signOut = () => {
  accessToken    = null;
  usingMsalToken = false;
  refreshKey     = null;
  localStorage.removeItem('classifi_access_token');
  localStorage.removeItem('classifi_using_msal');
  clearRoaming();
  const msgEl = document.getElementById('auth-message');
  if (msgEl) msgEl.textContent = 'Sign in with your Microsoft account to get started and sync your rules across all your devices.';
  document.getElementById('auth-screen')!.style.display = 'flex';
  document.getElementById('main-app')!.style.display    = 'none';
  toast('Signed out');
};

function showAuthScreen() {
  document.getElementById('auth-screen')!.style.display = 'flex';
  document.getElementById('main-app')!.style.display    = 'none';
}

async function showMainApp() {
  document.getElementById('auth-screen')!.style.display = 'none';
  document.getElementById('main-app')!.style.display    = 'flex';
  document.getElementById('user-email')!.textContent    = userEmail;
  const acctEmail = document.getElementById('account-email');
  if (acctEmail) acctEmail.textContent = userEmail;
  // Set correct initial status pill state from saved settings
  const s = getSettings();
  const sp = document.getElementById('status-pill')!;
  sp.textContent = s.enabled ? '● Active' : '○ Paused';
  sp.className   = 'status-pill' + (s.enabled ? '' : ' paused');
  await loadRules();
  loadRuleSuggestions();
}

/* ══ API ══ */
async function apiGet(path: string) {
  await ensureToken();
  const res = await fetch(`${API_BASE}${path}`, { headers: { Authorization: `Bearer ${accessToken}` } });
  if (res.status === 401) { triggerReAuth('Your session has expired. Please sign in again.'); throw new Error('Session expired'); }
  if (!res.ok) throw new Error(`API error ${res.status}`);
  return res.json();
}

async function apiPost(path: string, body: any) {
  await ensureToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  if (res.status === 401) { triggerReAuth('Your session has expired. Please sign in again.'); throw new Error('Session expired'); }
  if (!res.ok) throw new Error(`API error ${res.status}`);
  return res.json();
}

async function apiDelete(path: string) {
  await ensureToken();
  const res = await fetch(`${API_BASE}${path}`, { method: 'DELETE', headers: { Authorization: `Bearer ${accessToken}` } });
  if (res.status === 401) { triggerReAuth('Your session has expired. Please sign in again.'); throw new Error('Session expired'); }
  if (!res.ok) throw new Error(`API error ${res.status}`);
}

/* ══ Rules ══ */
async function loadRules() {
  try {
    rulesCache = await apiGet('/rules');
    renderRules();
  } catch(e) {
    toast('Could not load rules. Check connection.');
    rulesCache = [];
    renderRules();
  }
}

(window as any).addRule = async () => {
  const type  = (document.getElementById('rule-type') as HTMLSelectElement).value;
  const value = (document.getElementById('rule-value') as HTMLInputElement).value.trim().toLowerCase();
  const cats  = parseCats((document.getElementById('rule-category') as HTMLInputElement).value);
  if (!value)       { toast('Please enter a value to match.'); return; }
  if (!cats.length) { toast('Please enter at least one category.'); return; }
  if (rulesCache.some(r => r.type === type && r.value === value)) { toast('Rule already exists.'); return; }
  if (!isLicensed && rulesCache.length >= FREE_RULE_LIMIT) {
    toast(`Free tier limit: ${FREE_RULE_LIMIT} rules max. Upgrade to add more.`);
    return;
  }
  try {
    const saved = await apiPost('/rules', { type, value, categories: cats, enabled: true });
    rulesCache.push(saved);
    (document.getElementById('rule-value') as HTMLInputElement).value    = '';
    (document.getElementById('rule-category') as HTMLInputElement).value = '';
    renderRules();
    toast('Rule added ✓');
  } catch(e) { toast('Failed to save rule.'); }
};

(window as any).deleteRule = async (id: string) => {
  try {
    // Find rule before deleting so we can unlearn its signals
    const rule = rulesCache.find(r => r.id === id);
    await apiDelete(`/rules?id=${id}`);
    rulesCache = rulesCache.filter(r => r.id !== id);
    renderRules();
    toast('Rule deleted');
    // Unlearn the Bayesian signals from this rule
    if (rule && rule.categories?.length) {
      untrainOnRemove(rule.value, '', rule.categories);
    }
  } catch(e) { toast('Failed to delete rule.'); }
};

(window as any).toggleRule = async (id: string) => {
  const rule = rulesCache.find(r => r.id === id);
  if (!rule) return;
  rule.enabled = !rule.enabled;
  try { await apiPost('/rules', rule); renderRules(); } catch(e) { toast('Failed to update rule.'); }
};

(window as any).startEdit  = (id: string) => { editingId = id; renderRules(); };
(window as any).cancelEdit = () => { editingId = null; renderRules(); };

(window as any).saveEdit = async (id: string) => {
  const value = (document.getElementById('edit-value-' + id) as HTMLInputElement).value.trim().toLowerCase();
  const cats  = parseCats((document.getElementById('edit-cats-' + id) as HTMLInputElement).value);
  if (!value || !cats.length) { toast('Both fields required.'); return; }
  const rule = rulesCache.find(r => r.id === id);
  if (!rule) return;
  rule.value = value; rule.categories = cats;
  try { await apiPost('/rules', rule); editingId = null; renderRules(); toast('Rule updated ✓'); }
  catch(e) { toast('Failed to update rule.'); }
};


const RULE_SUGGESTIONS_CACHE_KEY = 'classifi_rule_suggestions';
const RULE_SUGGESTIONS_TTL_MS    = 60 * 60 * 1000; // 1 hour

async function loadRuleSuggestions() {
  // Check cache first
  try {
    const cached = localStorage.getItem(RULE_SUGGESTIONS_CACHE_KEY);
    if (cached) {
      const { timestamp, suggestions } = JSON.parse(cached);
      if (Date.now() - timestamp < RULE_SUGGESTIONS_TTL_MS) {
        renderRuleSuggestions(suggestions);
        return;
      }
    }
  } catch (e) { /* ignore cache errors */ }

  // Fetch fresh from server
  try {
    await ensureToken();
    const res = await fetch(`${API_BASE}/ruleSuggestions`, {
      headers: { Authorization: `Bearer ${accessToken}` }
    });
    if (!res.ok) return;
    const data = await res.json();
    // Cache results
    localStorage.setItem(RULE_SUGGESTIONS_CACHE_KEY, JSON.stringify({
      timestamp:   Date.now(),
      suggestions: data.suggestions || []
    }));
    renderRuleSuggestions(data.suggestions || []);
  } catch(e) {
    console.warn('Failed to load rule suggestions:', e);
  }
}

function renderRuleSuggestions(suggestions: any[]) {
  const el = document.getElementById('rule-suggestions');
  if (!el) return;
  if (!suggestions.length) { el.innerHTML = ''; return; }

  el.innerHTML = `
    <div style="padding:10px 14px 0;">
      <div style="font-size:10px;font-weight:600;letter-spacing:0.8px;text-transform:uppercase;color:var(--warn);margin-bottom:8px;font-family:var(--mono);display:flex;align-items:center;gap:6px;">
        💡 Suggested Rules
        <span style="font-size:9px;color:var(--muted);font-weight:400;">based on your recent inbox</span>
      </div>
      ${suggestions.map((s: any) => `
        <div class="rule-card" style="margin-bottom:5px;border-color:rgba(251,191,36,0.3);">
          <span class="type-badge ${s.type === 'sender_exact' ? 'b-sender' : 'b-domain'}" style="flex-shrink:0;">
            ${s.type === 'sender_exact' ? 'sender' : 'domain'}
          </span>
          <div class="rule-content">
            <div class="rule-value">${esc(s.value)}</div>
            <div style="font-size:9px;color:var(--muted);font-family:var(--mono);">${esc(s.display)}</div>
          </div>
          <button class="btn-accent" style="padding:3px 8px;font-size:10px;white-space:nowrap;"
            onclick="addSuggestedRule('${jsArg(s.type)}','${jsArg(s.value)}')">+ Add</button>
          <button class="icon-btn" style="margin-left:2px;"
            onclick="dismissSuggestion('${jsArg(s.value)}')">✕</button>
        </div>
      `).join('')}
    </div>
  `;
}

(window as any).addSuggestedRule = async (type: string, value: string) => {
  // Show category picker inline
  const cat = prompt(`Add "${value}" to which category? (e.g. Work, Finance)`);
  if (!cat || !cat.trim()) return;
  const categories = parseCats(cat);
  if (!categories.length) return;
  try {
    const saved = await apiPost('/rules', { type, value, categories, enabled: true });
    rulesCache.push(saved);
    // Invalidate cache so suggestion disappears
    localStorage.removeItem(RULE_SUGGESTIONS_CACHE_KEY);
    renderRules();
    toast(`Rule added: ${value} → ${categories.join(', ')} ✓`);
    // Re-load suggestions
    loadRuleSuggestions();
  } catch(e) { toast('Failed to add rule.'); }
};

(window as any).dismissSuggestion = (value: string) => {
  try {
    const cached = localStorage.getItem(RULE_SUGGESTIONS_CACHE_KEY);
    if (cached) {
      const data = JSON.parse(cached);
      data.suggestions = data.suggestions.filter((s: any) => s.value !== value);
      localStorage.setItem(RULE_SUGGESTIONS_CACHE_KEY, JSON.stringify(data));
      renderRuleSuggestions(data.suggestions);
    }
  } catch(e) { /* ignore */ }
};

function renderRules() {
  const query = ((document.getElementById('rule-search') as HTMLInputElement)?.value || '').toLowerCase().trim();
  let rules = rulesCache;
  if (query) rules = rules.filter(r => r.value.includes(query) || (r.categories||[]).some((c: string) => c.toLowerCase().includes(query)));
  const el = document.getElementById('rules-list')!;
  if (!rules.length) {
    el.innerHTML = rulesCache.length
      ? '<div class="empty-state"><div class="icon">🔍</div><p>No rules match your search.</p></div>'
      : '<div class="empty-state"><div class="icon">🏷️</div><p>No rules yet.<br/>Add one above or use the Learn tab.</p></div>';
    return;
  }
  const bc: any = { sender_exact: 'b-sender', sender_domain: 'b-domain', subject_contains: 'b-subject' };
  const bl: any = { sender_exact: 'Sender', sender_domain: 'Domain', subject_contains: 'Subject' };
  el.innerHTML = rules.map(r => {
    const cats  = r.categories || [];
    const pills = cats.map((c: string) => `<span class="cat-pill">${esc(c)}</span>`).join('');
    const isEditing = editingId === r.id;
    return `
    <div class="rule-card ${r.enabled ? '' : 'disabled'} ${isEditing ? 'editing' : ''}">
      <span class="type-badge ${bc[r.type]}">${bl[r.type]}</span>
      <div class="rule-content">
        ${isEditing ? `
          <div class="edit-form">
            <input type="text" id="edit-value-${r.id}" value="${esc(r.value)}" placeholder="Match value" />
            <input type="text" id="edit-cats-${r.id}" value="${esc(cats.join(', '))}" placeholder="Categories (comma-separated)" />
            <div class="edit-actions">
              <button class="btn-accent" onclick="saveEdit('${jsArg(r.id)}')">Save</button>
              <button class="btn-ghost" onclick="cancelEdit()">Cancel</button>
            </div>
          </div>
        ` : `
          <div class="rule-value">${esc(r.value)}</div>
          <div class="rule-cats">${pills}</div>
        `}
      </div>
      <div class="rule-actions">
        ${!isEditing ? `<button class="icon-btn edit" onclick="startEdit('${jsArg(r.id)}')" title="Edit">✎</button>` : ''}
        <button class="icon-btn ${r.enabled ? 'toggle-on' : 'toggle-off'}" onclick="toggleRule('${jsArg(r.id)}')">${r.enabled ? '●' : '○'}</button>
        <button class="icon-btn danger" onclick="deleteRule('${jsArg(r.id)}')">✕</button>
      </div>
    </div>`;
  }).join('');
}

/* ══ Learn ══ */
(window as any).refreshLearnPanel = () => {
  (window as any).hideMatchPicker();
  try {
    const item = Office.context.mailbox.item;
    if (!item) throw new Error();
    const email   = (item as any).from ? (item as any).from.emailAddress : '';
    const domain  = email.includes('@') ? email.split('@')[1] : '';
    const subject = (item as any).subject || '(no subject)';
    learnState = { sender: email.toLowerCase(), domain: domain.toLowerCase(), subject };
    document.getElementById('learn-from')!.textContent    = email || '—';
    document.getElementById('learn-subject')!.textContent = subject;
    document.getElementById('apply-info')!.textContent    = email || 'Open an email to apply rules';
    (document.getElementById('apply-btn') as HTMLButtonElement).disabled = !email;
    // Load Bayesian suggestions for this email
    loadSuggestions(email, subject);
  } catch(e) {
    learnState = { sender: '', domain: '', subject: '' };
    document.getElementById('learn-from')!.textContent    = '— open an email to begin —';
    document.getElementById('learn-subject')!.textContent = '—';
    document.getElementById('apply-info')!.textContent    = 'Open an email to apply rules';
    (document.getElementById('apply-btn') as HTMLButtonElement).disabled = true;
    document.getElementById('learn-suggestions')!.innerHTML = '';
  }
};



async function untrainOnRemove(sender: string, subject: string, categories: string[]) {
  try {
    await ensureToken();
    await fetch(`${API_BASE}/train`, {
      method:  'POST',
      headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
      body:    JSON.stringify({ sender, subject, categories, unlearn: true })
    });
    console.log('Untrained model for removed categories:', categories);
  } catch(e) {
    console.warn('Untraining failed:', e);
  }
}

async function loadSuggestions(sender: string, subject: string) {
  const el = document.getElementById('learn-suggestions')!;
  el.innerHTML = '';
  if (!sender) return;
  try {
    await ensureToken();
    const suggestLevel = getSettings().suggestLevel || 'medium';
    const thresholdMap: Record<string, number> = { low: 35, medium: 50, high: 70 };
    const threshold = thresholdMap[suggestLevel] || 50;
    const params = new URLSearchParams({ sender, subject, threshold: String(threshold) });
    const res = await fetch(`${API_BASE}/suggestions?${params}`, {
      headers: { Authorization: `Bearer ${accessToken}` }
    });
    if (!res.ok) return;
    const data = await res.json();
    if (!data.suggestions || !data.suggestions.length) return;
    el.innerHTML = `
      <div style="padding:10px 14px 4px;">
        <div style="font-size:10px;font-weight:600;letter-spacing:0.8px;text-transform:uppercase;color:var(--muted);margin-bottom:8px;font-family:var(--mono);">✦ Classifi Suggests</div>
        ${data.suggestions.map((s: any) => `
          <div class="rule-card" style="margin-bottom:5px;cursor:pointer;">
            <div class="rule-content">
              <div class="rule-value">${esc(s.category)}</div>
              <div style="font-size:9px;color:var(--muted);font-family:var(--mono);">${s.confidence}% confidence</div>
            </div>
            <div style="display:flex;gap:4px;flex-shrink:0;">
              <button class="btn-ghost" style="padding:3px 9px;font-size:10px;" onclick="addSuggestion('${jsArg(s.category)}')" title="Add this category and keep existing ones">Add</button>
              <button class="btn-accent" style="padding:3px 9px;font-size:10px;" onclick="fixSuggestion('${jsArg(s.category)}')" title="Set to only this category (corrects a wrong tag)">Fix</button>
            </div>
          </div>
        `).join('')}
      </div>
    `;
  } catch(e) {
    console.warn('Failed to load suggestions:', e);
  }
}

// Apply a category to the current email.
//   mode 'add'  -> add this category, keep existing ones (multi-tagging), and train it.
//   mode 'fix'  -> set the email to ONLY this category: remove + unlearn the others, then add + train this one.
async function applyCategory(category: string, mode: 'add' | 'fix') {
  try {
    const item = Office.context.mailbox.item as any;
    if (!item) { toast('No email selected.'); return; }
    const cats = [category];
    const addAndTrain = (toRemove: string[]) => {
      ensureMasterCategories(cats, () => {
        item.categories.addAsync(cats, (result: any) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            toast(`${mode === 'fix' ? 'Set to' : 'Added'}: ${category} \u2713`);
            trainOnApply(learnState.sender, learnState.subject, cats);
            if (toRemove.length) untrainOnRemove(learnState.sender, learnState.subject, toRemove);
            document.getElementById('learn-suggestions')!.innerHTML = '';
          } else {
            console.error('categories.addAsync failed:', result.error?.message, result.error?.code);
            toast('Could not apply: ' + (result.error?.message || result.error?.code || 'unknown error'));
          }
        });
      });
    };

    if (mode === 'add') { addAndTrain([]); return; }

    // mode 'fix' — replace any other categories on the email and unlearn them.
    item.categories.getAsync((getRes: any) => {
      const current: string[] = (getRes && getRes.status === Office.AsyncResultStatus.Succeeded ? (getRes.value || []) : [])
        .map((c: any) => (c && c.displayName) ? c.displayName : c)
        .filter(Boolean);
      const toRemove = current.filter((c: string) => c !== category);
      if (toRemove.length) {
        item.categories.removeAsync(toRemove, () => addAndTrain(toRemove));
      } else {
        addAndTrain([]);
      }
    });
  } catch(e) { toast('Error applying category.'); }
}
(window as any).addSuggestion = (category: string) => applyCategory(category, 'add');
(window as any).fixSuggestion = (category: string) => applyCategory(category, 'fix');
(window as any).applySuggestion = (category: string) => applyCategory(category, 'fix'); // backward-compat alias

(window as any).showMatchPicker = () => {
  const cats = parseCats((document.getElementById('learn-category') as HTMLInputElement).value);
  if (!cats.length) { toast('Enter at least one category first.'); return; }
  if (!learnState.sender) { toast('No email selected. Open an email first.'); return; }
  const opts = [
    { type: 'sender_exact',     badge: 'b-sender',  label: 'Exact sender',    sub: learnState.sender },
    { type: 'sender_domain',    badge: 'b-domain',  label: 'Entire domain',   sub: '@' + learnState.domain },
    { type: 'subject_contains', badge: 'b-subject', label: 'Subject keyword', sub: learnState.subject },
  ];
  document.getElementById('match-options')!.innerHTML = opts.map(o => `
    <div class="match-opt" onclick="commitLearn('${jsArg(o.type)}')">
      <span class="match-opt-badge type-badge ${o.badge}">${o.label}</span>
      <div style="flex:1;min-width:0;"><div class="match-opt-sub">${esc(o.sub)}</div></div>
    </div>`).join('');
  document.getElementById('match-picker')!.style.display = 'block';
};

(window as any).hideMatchPicker = () => { document.getElementById('match-picker')!.style.display = 'none'; };

(window as any).commitLearn = async (type: string) => {
  const cats = parseCats((document.getElementById('learn-category') as HTMLInputElement).value);
  let value = '';
  if (type === 'sender_exact')     value = learnState.sender;
  if (type === 'sender_domain')    value = learnState.domain;
  if (type === 'subject_contains') {
    const SUBJECT_STOP_WORDS = new Set([
      'your','this','that','from','with','have','been','will','they','them',
      'their','what','when','where','which','about','would','could','should',
      'just','into','over','also','back','more','some','than','then','very',
      'here','there','these','those','each','much','such','like','make',
      'take','know','need','only','even','most','well','both','does','done',
      'dear','hello','thanks','thank','please','regards','best','regards',
      'news','today','week','month','year','time','days','just','still',
      'update','updates','account','email','message','notification','info',
      'important','please','click','here','view','read','open','reply',
      'unsubscribe','privacy','policy','terms','service','support','help'
    ]);
    const words = learnState.subject
      .toLowerCase()
      .replace(/[^a-z0-9 ]/g, ' ')
      .split(/\s+/)
      .filter((w: string) => w.length > 3 && !SUBJECT_STOP_WORDS.has(w));
    // Pick the most meaningful word — prefer longer/less common words, skip first if possible
    const sorted = [...words].sort((a, b) => b.length - a.length);
    value = sorted[0] || learnState.subject.toLowerCase().replace(/[^a-z0-9 ]/g,' ').trim().substring(0, 20);
  }
  if (!value) { toast('Could not determine match value.'); return; }
  const existing = rulesCache.find(r => r.type === type && r.value === value);
  // Apply free tier limit for new rules (updating existing rules is always allowed)
  if (!existing && !isLicensed && rulesCache.length >= FREE_RULE_LIMIT) {
    toast(`Free tier limit: ${FREE_RULE_LIMIT} rules max. Upgrade to add more.`);
    (window as any).hideMatchPicker();
    return;
  }
  try {
    if (existing) {
      existing.categories = [...new Set([...(existing.categories || []), ...cats])];
      await apiPost('/rules', existing);
      toast('Updated rule ✓');
    } else {
      const saved = await apiPost('/rules', { type, value, categories: cats, enabled: true });
      rulesCache.push(saved);
      toast('Rule created ✓');
    }
    // Train Bayesian model on Learn tab categorization
    trainOnApply(learnState.sender, learnState.subject, cats);
    (document.getElementById('learn-category') as HTMLInputElement).value = '';
    (window as any).hideMatchPicker();
  } catch(e) { toast('Failed to save rule.'); }
};

(window as any).applyToCurrent = () => {
  try {
    const item    = Office.context.mailbox.item as any;
    const sender  = item.from ? item.from.emailAddress.toLowerCase() : '';
    const domain  = sender.includes('@') ? sender.split('@')[1] : '';
    const subject = (item.subject || '').toLowerCase();
    const rules   = rulesCache.filter(r => r.enabled);
    let matched: string[] = [];
    rules.forEach((r: any) => {
      const v = r.value.toLowerCase();
      if (r.type === 'sender_exact'     && sender === v)        matched.push(...(r.categories||[]));
      if (r.type === 'sender_domain'    && domain === v)        matched.push(...(r.categories||[]));
      if (r.type === 'subject_contains' && subject.includes(v)) matched.push(...(r.categories||[]));
    });
    matched = [...new Set(matched)];
    if (!matched.length) { toast('No matching rules for this email.'); return; }
    // OWA requires categories to exist in the master list before applying to an item.
    // Ensure all matched categories are registered first, then apply.
    ensureMasterCategories(matched, () => {
      item.categories.addAsync(matched, (result: any) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          toast(`Applied: ${matched.join(', ')} ✓`);
          // Train Bayesian model on confirmed manual apply
          const senderFull = item.from ? item.from.emailAddress : '';
          trainOnApply(senderFull, item.subject || '', matched);
        } else {
          console.error('categories.addAsync failed:', result.error?.message, result.error?.code);
          toast('Could not apply: ' + (result.error?.message || result.error?.code || 'unknown error'));
        }
      });
    });
  } catch(e) { toast('Error applying categories.'); }
};

/* ══ Activity ══ */
function getActivity() { try { return JSON.parse(localStorage.getItem('classifi_activity') || '[]'); } catch(e) { return []; } }

(window as any).renderActivity = () => {
  const log = getActivity();
  const el  = document.getElementById('activity-list')!;
  if (!log.length) { el.innerHTML = '<div class="empty-state"><div class="icon">📋</div><p>No activity yet.<br/>Categorized emails will appear here.</p></div>'; return; }
  el.innerHTML = log.map((a: any) => {
    const cats = Array.isArray(a.categories) ? a.categories : (a.category ? [a.category] : []);
    return `<div class="act-item"><div class="act-top">${cats.map((c: string) => `<span class="act-cat">${esc(c)}</span>`).join('')}<span class="act-time">${fmtTime(a.timestamp)}</span></div><div class="act-subject">${esc(a.subject)}</div><div class="act-sender">${esc(a.sender)}</div></div>`;
  }).join('');
};

(window as any).clearActivity = () => { localStorage.removeItem('classifi_activity'); (window as any).renderActivity(); toast('Activity cleared'); };

/* ══ Settings ══ */
function getSettings() { try { return JSON.parse(localStorage.getItem('classifi_settings') || '{"enabled":true,"subjects":true}'); } catch(e) { return {enabled:true,subjects:true}; } }
function saveSettings(s: any) { localStorage.setItem('classifi_settings', JSON.stringify(s)); }


(window as any).setSuggestLevel = (level: string) => {
  const s = getSettings();
  s.suggestLevel = level;
  saveSettings(s);
  // Update UI
  ['low','medium','high'].forEach(l => {
    const el = document.getElementById(`suggest-${l}`);
    if (el) el.className = 'suggest-opt' + (l === level ? ' active' : '');
  });
};


let bulkPollInterval: any = null;

async function pollBulkJob() {
  try {
    await ensureToken();
    const res  = await fetch(`${API_BASE}/jobStatus`, { headers: { Authorization: `Bearer ${accessToken}` } });
    if (!res.ok) return;
    const data = await res.json();

    const btn      = document.getElementById('bulk-btn') as HTMLButtonElement;
    const progress = document.getElementById('bulk-progress')!;
    const bar      = document.getElementById('bulk-bar')!;
    const label    = document.getElementById('bulk-label')!;
    const result   = document.getElementById('bulk-result')!;

    if (data.status === 'running') {
      const pct = data.total > 0 ? Math.round((data.processed / data.total) * 100) : 0;
      bar.style.width    = `${pct}%`;
      label.textContent  = `${data.processed} of ${data.total} emails processed...`;
      progress.className = 'progress-wrap visible';
      btn.className      = 'bulk-btn running';
      btn.disabled       = true;
      (document.getElementById('bulk-btn-label') as HTMLElement).textContent = 'Running...';

    } else if (data.status === 'complete') {
      clearInterval(bulkPollInterval);
      bulkPollInterval = null;
      bar.style.width    = '100%';
      label.textContent  = 'Complete!';
      setTimeout(() => { progress.className = 'progress-wrap'; }, 2000);
      btn.className      = 'bulk-btn';
      btn.disabled       = !isLicensed;
      (document.getElementById('bulk-btn-label') as HTMLElement).textContent = 'Categorize Inbox';
      result.textContent = `✓ Applied categories to ${data.applied} of ${data.total} emails`;
      result.style.display = 'block';
      result.style.color   = 'var(--success)';

    } else if (data.status === 'error') {
      clearInterval(bulkPollInterval);
      bulkPollInterval = null;
      progress.className = 'progress-wrap';
      btn.className      = 'bulk-btn';
      btn.disabled       = !isLicensed;
      (document.getElementById('bulk-btn-label') as HTMLElement).textContent = 'Categorize Inbox';
      result.textContent   = `Error: ${data.error || 'Something went wrong'}`;
      result.style.display = 'block';
      result.style.color   = 'var(--danger)';
    }
  } catch(e) {
    console.warn('Poll failed:', e);
  }
}

(window as any).startBulkCategorize = async () => {
  try {
    await ensureToken();

    const btn    = document.getElementById('bulk-btn') as HTMLButtonElement;
    const result = document.getElementById('bulk-result')!;
    result.style.display = 'none';
    btn.disabled = true;
    btn.className = 'bulk-btn running';
    (document.getElementById('bulk-btn-label') as HTMLElement).textContent = 'Starting...';
    document.getElementById('bulk-progress')!.className = 'progress-wrap visible';
    document.getElementById('bulk-label')!.textContent  = 'Connecting...';
    document.getElementById('bulk-bar')!.style.width    = '0%';

    const res = await fetch(`${API_BASE}/bulkCategorize`, {
      method:  'POST',
      headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' }
    });

    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }

    // Start polling for progress
    if (bulkPollInterval) clearInterval(bulkPollInterval);
    bulkPollInterval = setInterval(pollBulkJob, 2000);

  } catch(e) {
    toast('Failed to start bulk categorization.');
    const btn = document.getElementById('bulk-btn') as HTMLButtonElement;
    btn.disabled  = !isLicensed;
    btn.className = 'bulk-btn';
    (document.getElementById('bulk-btn-label') as HTMLElement).textContent = 'Categorize Inbox';
    document.getElementById('bulk-progress')!.className = 'progress-wrap';
  }
};

(window as any).renderSettings = () => {
  const s = getSettings();
  document.getElementById('toggle-enabled')!.className  = 'toggle ' + (s.enabled  ? 'on' : '');
  document.getElementById('toggle-subjects')!.className = 'toggle ' + (s.subjects ? 'on' : '');
  document.getElementById('rule-count')!.textContent     = String(rulesCache.length);
  document.getElementById('activity-count')!.textContent = String(getActivity().length);
  const sp = document.getElementById('status-pill')!;
  sp.textContent = s.enabled ? '● Active' : '○ Paused';
  sp.className   = 'status-pill' + (s.enabled ? '' : ' paused');
  // Render AI suggestion level
  const level = (getSettings().suggestLevel || 'medium') as string;
  ['low','medium','high'].forEach((l: string) => {
    const el = document.getElementById(`suggest-${l}`);
    if (el) el.className = 'suggest-opt' + (l === level ? ' active' : '');
  });
  updateLicenseUI();
};

(window as any).toggleSetting = (key: string) => {
  const s = getSettings(); s[key] = !s[key]; saveSettings(s); (window as any).renderSettings();
  const sp = document.getElementById('status-pill')!;
  sp.textContent = s.enabled ? '● Active' : '○ Paused';
  sp.className = 'status-pill' + (s.enabled ? '' : ' paused');
};

(window as any).exportRules = () => {
  const blob = new Blob([JSON.stringify(rulesCache, null, 2)], { type: 'application/json' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = 'classifi-rules.json'; a.click(); URL.revokeObjectURL(url);
};

(window as any).importRules = () => { document.getElementById('import-file')!.click(); };

(window as any).handleImport = async (e: Event) => {
  const file = (e.target as HTMLInputElement).files?.[0];
  if (!file) return;
  const reader = new FileReader();
  reader.onload = async (ev) => {
    try {
      const imported = JSON.parse((ev.target as FileReader).result as string);
      if (!Array.isArray(imported)) throw new Error();
      let added = 0;
      for (const r of imported) {
        if (!r.type || !r.value) continue;
        if (!r.categories && r.category) r.categories = [r.category];
        if (!rulesCache.some(e => e.type === r.type && e.value === r.value)) {
          const saved = await apiPost('/rules', r); rulesCache.push(saved); added++;
        }
      }
      renderRules(); toast(`Imported ${added} rule${added !== 1 ? 's' : ''} ✓`);
    } catch(err) { toast('Invalid JSON file.'); }
    (e.target as HTMLInputElement).value = '';
  };
  reader.readAsText(file);
};

(window as any).confirmReset = async () => {
  // Note: confirm() is blocked in Outlook task panes — reset proceeds directly
  try {
    for (const r of rulesCache) await apiDelete(`/rules?id=${r.id}`);
    rulesCache = [];
    localStorage.removeItem('classifi_activity');
    localStorage.removeItem('classifi_settings');
    renderRules(); (window as any).renderSettings(); toast('All data cleared');
  } catch(e) { toast('Failed to reset data.'); }
};

(window as any).activateLicense = async () => {
  const input = (document.getElementById('license-key-input') as HTMLInputElement);
  const errEl = document.getElementById('license-error')!;
  const key   = input?.value?.trim().toUpperCase();
  errEl.style.display = 'none';

  if (!key || key.length < 10) {
    errEl.textContent = 'Please enter a valid license key.';
    errEl.style.display = 'block';
    return;
  }

  const btn = document.querySelector('#license-section .btn-accent') as HTMLButtonElement;
  if (btn) { btn.textContent = 'Checking...'; btn.disabled = true; }

  try {
    await ensureToken();
    const res = await fetch(`${STELARO_BASE}/validateLicense`, {
      method:  'POST',
      headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json', 'X-Stelaro-Product': PRODUCT_ID },
      body:    JSON.stringify({ licenseKey: key })
    });
    const data = await res.json();

    if (data.valid) {
      isLicensed  = true;
      licenseType = data.licenseType || 'lifetime';
      updateLicenseUI();
      toast('License activated ✓');
      // Start webhook now that licensed
      registerWebhook();
    } else {
      errEl.textContent = data.reason || 'Invalid license key.';
      errEl.style.display = 'block';
      if (btn) { btn.textContent = 'Activate'; btn.disabled = false; }
    }
  } catch(e) {
    errEl.textContent = 'Could not validate license. Check your connection.';
    errEl.style.display = 'block';
    if (btn) { btn.textContent = 'Activate'; btn.disabled = false; }
  }
};

/* ══ Tabs ══ */
(window as any).switchTab = (tab: string, event: Event) => {
  document.querySelectorAll('.tab').forEach(t => t.classList.remove('active'));
  document.querySelectorAll('.panel').forEach(p => p.classList.remove('active'));
  if (event) (event.target as HTMLElement).classList.add('active');
  document.getElementById('panel-' + tab)!.classList.add('active');
  if (tab === 'rules')    { renderRules(); loadRuleSuggestions(); }
  if (tab === 'learn')    (window as any).refreshLearnPanel();
  if (tab === 'activity') (window as any).renderActivity();
  if (tab === 'settings') (window as any).renderSettings();
};

/* ══ Utils ══ */
function parseCats(str: string) { return str.split(',').map(s => s.trim()).filter(Boolean); }
function esc(s: any) { return String(s||'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;'); }
// Escape a value for safe use inside a single-quoted JS string within a double-quoted
// HTML attribute, e.g. onclick="fn('<value>')". JS-escapes backslash and single quote,
// HTML-escapes & " < >. Use this (not esc) for any value interpolated into an inline handler.
function jsArg(s: any): string {
  return String(s == null ? '' : s)
    .replace(/\\/g, '\\\\')
    .replace(/'/g, "\\'")
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
function fmtTime(iso: string) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US',{month:'short',day:'numeric'}) + ' ' + d.toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit'});
}
function toast(msg: string) {
  const el = document.getElementById('toast')!;
  el.textContent = msg; el.classList.add('show');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => el.classList.remove('show'), 2400);
}

/* ══ Init ══ */
function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return (payload.exp * 1000) < Date.now();
  } catch {
    return true;
  }
}

Office.onReady(async () => {
  // Restore the durable session from the mailbox (survives restart + roams across devices).
  const roaming = loadRoaming();
  const email   = Office.context.mailbox?.userProfile?.emailAddress || '';
  if (roaming.refreshKey && roaming.email === email) refreshKey = roaming.refreshKey;

  // 1) Still-valid access token (roaming first, then localStorage) -- use it directly.
  const savedToken = (roaming.accessToken && roaming.email === email)
    ? roaming.accessToken
    : localStorage.getItem('classifi_access_token');
  if (savedToken && !isTokenExpired(savedToken)) {
    console.log('[Classifi] Using saved access token');
    accessToken    = savedToken;
    usingMsalToken = true;
    await initAuthWithToken(savedToken);
    return;
  }

  // 2) Token expired but we have a refreshKey -- mint a new one silently. This is
  //    the path that makes "sign in once" hold across days and weekends.
  if (refreshKey) {
    console.log('[Classifi] Access token expired -- refreshing silently');
    const refreshed = await refreshAccessToken();
    if (refreshed) { await initAuthWithToken(refreshed); return; }
    console.log('[Classifi] Silent refresh failed -- session revoked/expired');
  }

  // 3) Stale localStorage token with no refresh path -- clear it.
  if (localStorage.getItem('classifi_access_token')) {
    localStorage.removeItem('classifi_access_token');
    localStorage.removeItem('classifi_using_msal');
    accessToken    = null;
    usingMsalToken = false;
  }

  // 4) Last resort: silent SSO if the client supports it, else show the sign-in screen.
  try {
    const token = await (Office.auth as any).getAccessToken({
      allowSignInPrompt:  false,
      allowConsentPrompt: false
    });
    await initAuthWithToken(token);
  } catch (e: any) {
    const code = e?.code ?? 0;
    console.log('[Classifi] SSO failed (code ' + code + ') -- showing sign-in screen');
    document.getElementById('auth-screen')!.style.display = 'flex';
    document.getElementById('main-app')!.style.display    = 'none';
  }
});

async function initAuthWithToken(token: string): Promise<void> {
  accessToken = token;
  userEmail = Office.context.mailbox?.userProfile?.emailAddress || '—';
  await showMainApp();
  await checkLicense();
  if (isLicensed) {
    registerWebhook();
    if (!modelSeeded) { seedModel(); modelSeeded = true; }
  }
}
